import type { PrebidKeyValue } from '@schibsted-nmp/advertising-shared';

/**
 * Formats Prebid key-values into PubMatic DCTR (Deal Custom Targeting) string format.
 *
 * Per PubMatic Prebid.js docs: https://docs.prebid.org/dev-docs/bidders/pubmatic.html
 * DCTR format: "key1:abc|key2:123"
 * - Key-value separator: colon (:)
 * - Multiple keys: pipe-separated (|)
 * - Multiple values for same key: repeat key for each value (key1:val1|key1:val2)
 * - Special characters | and : are escaped with underscores
 *
 * @param targeting - Array of PrebidKeyValue objects
 * @returns DCTR-formatted string, or empty string if no targeting data
 *
 * @example
 * formatDctr([
 *   { key: "deal_id", value: ["123", "456"] },
 *   { key: "category", value: ["sports"] }
 * ])
 * // Returns: "deal_id:123|deal_id:456|category:sports"
 */
export function formatDctr(targeting: Array<PrebidKeyValue>): string {
  if (!targeting.length) return '';

  const pairs: Array<string> = [];

  for (const { key, value } of targeting) {
    if (!value.length) continue;

    for (const val of value) {
      // Escape special DCTR characters: pipe and colon
      const escapedValue = val.replace(/[|:]/g, '_');
      pairs.push(`${key}:${escapedValue}`);
    }
  }

  return pairs.join('|');
}
